import { Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppAuth } from "./auth";
import { Loading } from "./components/ui";
import { Shell } from "./components/Shell";
import { NotAuthorized } from "./components/NotAuthorized";
import { SignIn } from "./pages/SignIn";
import { Overview } from "./pages/Overview";
import { Members } from "./pages/Members";
import { MemberDetail } from "./pages/MemberDetail";
import { Doctors } from "./pages/Doctors";
import { DoctorDetail } from "./pages/DoctorDetail";
import { Applications } from "./pages/Applications";
import { Visits } from "./pages/Visits";
import { Payments } from "./pages/Payments";
import { EmrSync } from "./pages/EmrSync";
import { Compliance } from "./pages/Compliance";
import { Settings } from "./pages/Settings";
import { Team } from "./pages/Team";

function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, authz } = useAppAuth();
  // Gate order matters: session loading → signed-in check → SERVER authorization. The
  // shell renders only once the server confirms this account is authorized for the portal — never
  // on client-side sign-in alone. "checking" holds (no shell flash); "denied" → rejection wall.
  if (!isLoaded) return <Loading label="Checking your session…" />;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  if (authz === "checking") return <Loading label="Verifying access…" />;
  if (authz === "denied") return <NotAuthorized />;
  return <>{children}</>;
}

export function App() {
  return (
    <div className="command-app">
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<Overview />} />
          <Route path="members" element={<Members />} />
          <Route path="members/:id" element={<MemberDetail />} />
          <Route path="doctors" element={<Doctors />} />
          <Route path="doctors/:id" element={<DoctorDetail />} />
          <Route path="applications" element={<Applications />} />
          <Route path="visits" element={<Visits />} />
          <Route path="payments" element={<Payments />} />
          <Route path="emr" element={<EmrSync />} />
          <Route path="compliance" element={<Compliance />} />
          <Route path="settings" element={<Settings />} />
          <Route path="team" element={<Team />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
